import { useTranslation } from "@i18n";
import Text from "@CubeComponents/html/Text/Text";
import { linkStatusLabel } from "./externalLinks.helpers";
import type { ExternalLinkListItem } from "./externalLinks.api";

/**
 * The standard dot + tinted-pill status chip (QA r3 1a), matching the app's reference implementation
 * in `features/portal/components/NotificationsGridView/ExportedFilesTab.tsx`: ONE colour per state —
 * full opacity for the dot and the label, a light tint for the pill.
 *
 * Keyed by the DERIVED state, not the translated label, so locale changes can never unmap a colour.
 * The precedence mirrors `linkStatusLabel` (revoked ≻ admin-disabled ≻ expired ≻ active) — the two
 * must agree or the chip would tint one story and read another.
 */
const CHIP_COLORS = {
  revoked: { bg: "#FEF3F2", fg: "#B42318" },
  disabledByAdmin: { bg: "#FFFAEB", fg: "#B54708" },
  expired: { bg: "#F9FAFB", fg: "#475467" },
  active: { bg: "#ECFDF3", fg: "#027A48" },
} as const;

function statusKey(link: ExternalLinkListItem): keyof typeof CHIP_COLORS {
  if (link.revoked) return "revoked";
  if (link.disabledByAdmin) return "disabledByAdmin";
  if (link.expiresAt && link.expiresAt <= Date.now()) return "expired";
  return "active";
}

function ExternalLinkStatusChip({ link }: Readonly<{ link: ExternalLinkListItem }>) {
  const { t } = useTranslation();
  const colors = CHIP_COLORS[statusKey(link)];
  return (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.375rem",
        padding: "0.125rem 0.5rem",
        borderRadius: "1rem",
        backgroundColor: colors.bg,
        width: "fit-content",
      }}
    >
      <span style={{ width: "0.375rem", height: "0.375rem", borderRadius: "50%", backgroundColor: colors.fg }} />
      <Text style={{ color: colors.fg, fontSize: "0.75rem", fontWeight: 500 }}>{linkStatusLabel(t, link)}</Text>
    </span>
  );
}

export default ExternalLinkStatusChip;
